






























































import {
  computed,
  defineComponent,
  PropType,
  ref
} from '~/utils/nuxt3-migration'
import { toBase64 } from '~/utils/object'
import CSubtitleButton from '~/components/shared/configurable/button/CSubtitleButton.vue'
import ClassifiedOfferPrice from '~/components/car/offers/ClassifiedOfferPrice.vue'
import { defineComponentTranslations } from '~/utils/i18n'
import { ciSaleRequest as ciSaleRequestDuotone } from '~/icons/source/duotone/sale-request'
import { ciSaleRequest as ciSaleRequestRegular } from '~/icons/source/regular/sale-request'
import { useNamespacedStore } from '~/compositions/store'
import {
  CLASSIFIED_VIEW_NS,
  ClassifiedViewState
} from '~/store/modules/shared/classifieds/view/state'
import { useI18n } from '~/compositions/i18n'
import SaleRequestsService from '~/services/sale-requests/SaleRequestsService'
import { useDeps } from '~/compositions/dependency-container'
import { useSnackbar } from '~/compositions/snackbar'
import { handleError } from '~/utils/http'
import { AxiosError } from 'axios'
import { useLogger } from '~/compositions/logger'
import { AccessToggleName } from '~/models/user/access-toggles'
import { USER_NS, UserState } from '~/store/modules/shared/user/state'

export default defineComponent({
  components: { ClassifiedOfferPrice, CSubtitleButton },
  props: {
    saleRequests: {
      type: Object as PropType<any>,
      required: true
    }
  },
  setup() {
    const {
      state: classifiedViewState,
      dispatch: classifiedViewDispatch
    } = useNamespacedStore<ClassifiedViewState>(CLASSIFIED_VIEW_NS)
    const { getters: userGetters } = useNamespacedStore<UserState>(USER_NS)
    const { t } = useI18n()
    const [saleRequestsService] = useDeps(SaleRequestsService)
    const snackbar = useSnackbar()
    const logger = useLogger()

    const showSaleRequestManageModal = ref(false)
    const loading = ref(false)

    const classified = computed(() => classifiedViewState.classified)

    const userIsBuyer = computed(() =>
      userGetters('userHasAccessTo')(AccessToggleName.SALE_REQUESTS_BUY)
    )

    const newSaleRequestLink = computed(() => ({
      name: '__sale_requests_new',
      query: {
        classified: toBase64({
          price: classified.value.price.extra.raw_price,
          categoryId: classified.value.saleRequests.category,
          makeId: classified.value.saleRequests.makeId,
          registrationTo: classified.value.saleRequests.registrationTo,
          registrationFrom: classified.value.saleRequests.registrationFrom,
          mileageTo: classified.value.saleRequests.mileageTo,
          modelId: classified.value.saleRequests.modelId,
          fuelTypeId: classified.value.saleRequests.fuelTypeId,
          damage: classified.value.saleRequests.damage,
          automatic: classified.value.saleRequests.automatic
        })
      }
    }))

    const pendingSaleRequestId = computed(
      () => classified.value.saleRequests.pendingSaleRequestId
    )

    const hasPendingRequest = computed(() => !!pendingSaleRequestId.value)

    function onSaleRequestButtonClick() {
      if (!hasPendingRequest.value) {
        return
      }

      showSaleRequestManageModal.value = true
    }

    async function onCompletedButtonClick() {
      if (!confirm(t('are_you_sure_complete_sale_request'))) {
        return
      }

      loading.value = true

      try {
        await saleRequestsService.updateManagerSaleRequestStatus(
          pendingSaleRequestId.value,
          'completed'
        )
        snackbar.success(t('sale_completed_successfully'))
        showSaleRequestManageModal.value = false
        await classifiedViewDispatch('reloadClassifiedView')
      } catch (err) {
        handleError(err as AxiosError, snackbar, logger, t)
      } finally {
        loading.value = false
      }
    }

    async function onRejectedButtonClick() {
      if (!confirm(t('are_you_sure_reject_sale_request'))) {
        return
      }

      loading.value = true

      try {
        await saleRequestsService.updateManagerSaleRequestStatus(
          pendingSaleRequestId.value,
          'rejected'
        )
        snackbar.success(t('sale_rejected_successfully'))
        showSaleRequestManageModal.value = false
        await classifiedViewDispatch('reloadClassifiedView')
      } catch (err) {
        handleError(err as AxiosError, snackbar, logger, t)
      } finally {
        loading.value = false
      }
    }

    return {
      ciSaleRequestRegular,
      ciSaleRequestDuotone,
      newSaleRequestLink,
      showSaleRequestManageModal,
      hasPendingRequest,
      loading,
      userIsBuyer,
      onSaleRequestButtonClick,
      onCompletedButtonClick,
      onRejectedButtonClick
    }
  },
  i18n: defineComponentTranslations({
    create_buy_request: {
      en: 'Create buy request',
      el: 'Δημιουργία αιτήματος αγοράς'
    },
    for_vehicle_with_these_specifications: {
      en: 'For a vehicle with these specifications',
      el: 'Για όχημα με ίδια χαρακτηριστικά'
    },
    view_your_request: {
      en: 'View your request',
      el: 'Δες το αίτημά σου'
    },
    you_have_an_active_request: {
      en: 'You have an active request for this classified',
      el: 'Έχεις ένα ενεργό αίτημα για αυτή την αγγελία'
    },
    rejected: {
      en: 'Rejected',
      el: 'Απορρίφθηκε'
    }
  })
})
